import { useCallback, useEffect, useState } from "react";

import log from "@app/utils/logger";

import useTimeout from "./useTimeout";

const useCopyToClipboard = (tooltipTimeout = 1000) => {
  const [copied, setCopied] = useState<boolean>(false);
  const [supportsCopyToClipboard, setSupportsCopyToClipboard] = useState(
    Boolean(navigator?.clipboard)
  );

  useEffect(() => {
    // don't check permissions if we can't, or if we already know copy isn't supported
    if (!navigator || !navigator.permissions || !supportsCopyToClipboard) {
      return;
    }

    try {
      navigator.permissions
        .query({ name: "clipboard-write" as PermissionName })
        .then((result) => {
          setSupportsCopyToClipboard(result.state === "granted");
        })
        .catch(() => {
          // this permission only exists on Chromium browsers
          log.debug(
            "'clipboard-write' is not a permission name in this browser"
          );
        });
    } catch {
      log.debug("permission querying is not supported in this browser");
    }
  }, []);

  useTimeout(() => setCopied(false), tooltipTimeout, copied);

  const copyToClipboard = useCallback(
    (value: string) => {
      if (supportsCopyToClipboard) {
        navigator.clipboard.writeText(value).then(() => {
          setCopied(true);
        });
      }
    },
    [supportsCopyToClipboard]
  );

  return {
    copied,
    copyToClipboard,
    isSupported: supportsCopyToClipboard,
    disableCopy: !supportsCopyToClipboard,
  };
};

export default useCopyToClipboard;
